/**
 * Hook central da listagem operativa.
 * Encapsula: estado da grid, filtros, paginação, ordenação e debounce.
 * Os componentes apenas consomem — nenhum deles faz fetch diretamente.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";

import {
  FiltrosOperativa,
  OrderByColumn,
  SistemaItem,
  UsuarioOperativaItem,
} from "../interface/usuarioOperativa";

import {
  fetchUsuariosOperativa,
  fetchSistemas,
  OperativaServiceError,
} from "../services/usuarioOperativa.service";

// ---------- Constantes -------------------------------------------------

const FILTROS_INICIAIS: FiltrosOperativa = {
  page: 1,
  limit: 20,
  nome: "",
  cargo: "",
  nmUnidOperacional: "",
  idAcesso: "",
  base: "",
  sistemas: [],
  orderBy: "NOME",
  orderDir: "ASC",
};

/** ms de espera após o usuário parar de digitar antes de disparar o fetch */
const DEBOUNCE_MS = 800;

/**
 * Mínimo de caracteres por campo de texto antes de disparar o fetch.
 * nome/cargo exigem 3 (evitar chamadas prematuras em buscas abertas).
 * nmUnidOperacional e base exigem 2 (valores curtos como "PC", "4º BPM" são válidos).
 */
const MIN_CHARS_POR_CAMPO: Partial<Record<keyof FiltrosOperativa, number>> = {
  nome: 3,
  cargo: 3,
  nmUnidOperacional: 2,
  base: 2,
};

/**
 * Regex para detectar se o valor digitado no campo Nome/CPF é um CPF.
 * Critério: conter 3 ou mais dígitos consecutivos.
 */
const REGEX_CPF = /\d{3,}/;

const CAMPOS_TEXTO = Object.keys(MIN_CHARS_POR_CAMPO) as Array<
  keyof FiltrosOperativa
>;

// ---------- Tipos do retorno -------------------------------------------

export interface MetaPaginacao {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UseUsuariosOperativaReturn {
  filtros: FiltrosOperativa;
  data: UsuarioOperativaItem[];
  meta: MetaPaginacao;
  loading: boolean;
  /** true após a primeira busca bem-sucedida */
  iniciado: boolean;
  sistemasDisponiveis: SistemaItem[];
  atualizarFiltro<K extends keyof FiltrosOperativa>(
    chave: K,
    valor: FiltrosOperativa[K]
  ): void;
  trocarPagina(novaPagina: number): void;
  trocarLimitePorPagina(novoLimit: number): void;
  trocarOrdenacao(coluna: OrderByColumn): void;
  limparFiltros(): void;
}

// ---------- Hook -------------------------------------------------------

export function useUsuariosOperativa(): UseUsuariosOperativaReturn {
  const [filtros, setFiltros] = useState<FiltrosOperativa>(FILTROS_INICIAIS);
  const [data, setData] = useState<UsuarioOperativaItem[]>([]);
  const [meta, setMeta] = useState<MetaPaginacao>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [iniciado, setIniciado] = useState(false);
  const [sistemasDisponiveis, setSistemasDisponiveis] = useState<SistemaItem[]>(
    []
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Carrega a lista de sistemas uma única vez ao montar o hook
  useEffect(() => {
    const token = sessionStorage.getItem("@webSDS:token") ?? "";
    fetchSistemas(token)
      .then(setSistemasDisponiveis)
      .catch(() => {
        // Falha silenciosa — o dropdown ficará vazio; não bloqueia a listagem
      });
  }, []);

  // Mantém referência estável para não re-criar o useEffect desnecessariamente
  const buscar = useCallback(async (params: FiltrosOperativa) => {
    const token = sessionStorage.getItem("@webSDS:token") ?? "";
    setLoading(true);
    try {
      // Detecta se o campo "nome" contém um CPF (3+ dígitos consecutivos)
      // e redireciona para o parâmetro correto antes de enviar ao backend
      const isCpf = REGEX_CPF.test(params.nome);
      const paramsAjustados = {
        ...params,
        nome: isCpf ? "" : params.nome,
        cpf: isCpf ? params.nome : undefined,
      };

      const resposta = await fetchUsuariosOperativa(paramsAjustados, token);
      setData(resposta.data);
      setMeta({
        total: resposta.total,
        page: resposta.page,
        limit: resposta.limit,
        totalPages: resposta.totalPages,
      });
      setIniciado(true);
    } catch (err) {
      if (
        err instanceof OperativaServiceError &&
        err.tipo === "sessao_expirada"
      ) {
        toast.error("Sua sessão expirou. Efetue login novamente.", {
          position: toast.POSITION.TOP_CENTER,
        });
      } else if (
        err instanceof OperativaServiceError &&
        err.tipo === "sem_permissao"
      ) {
        toast.warning("Você não tem permissão para acessar esta listagem.", {
          position: toast.POSITION.TOP_CENTER,
        });
      } else {
        toast.error("Erro ao buscar usuários. Tente novamente.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Observa filtros: aplica debounce e garante mínimo de chars nos campos de texto
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Não dispara enquanto algum campo de texto está em digitação parcial (abaixo do mínimo)
    const temCampoAbaixoDoMinimo = CAMPOS_TEXTO.some((campo) => {
      const valor = filtros[campo] as string;
      const min = MIN_CHARS_POR_CAMPO[campo] ?? 2;
      return valor.length > 0 && valor.length < min;
    });

    if (!temCampoAbaixoDoMinimo) {
      debounceRef.current = setTimeout(() => {
        buscar(filtros);
      }, DEBOUNCE_MS);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filtros, buscar]);

  // Alterações de filtro (não-página): reseta para página 1
  const atualizarFiltro = useCallback(
    <K extends keyof FiltrosOperativa>(
      chave: K,
      valor: FiltrosOperativa[K]
    ) => {
      setFiltros((prev) => ({ ...prev, [chave]: valor, page: 1 }));
    },
    []
  );

  // Troca de página mantém todos os filtros intactos
  const trocarPagina = useCallback((novaPagina: number) => {
    setFiltros((prev) => ({ ...prev, page: novaPagina }));
  }, []);

  const trocarLimitePorPagina = useCallback((novoLimit: number) => {
    setFiltros((prev) => ({ ...prev, limit: novoLimit, page: 1 }));
  }, []);

  // Clique no header inverte direção se a coluna já é a atual, caso contrário ASC
  const trocarOrdenacao = useCallback((coluna: OrderByColumn) => {
    setFiltros((prev) => ({
      ...prev,
      orderBy: coluna,
      orderDir:
        prev.orderBy === coluna && prev.orderDir === "ASC" ? "DESC" : "ASC",
      page: 1,
    }));
  }, []);

  const limparFiltros = useCallback(() => {
    setFiltros(FILTROS_INICIAIS);
  }, []);

  return {
    filtros,
    data,
    meta,
    loading,
    iniciado,
    sistemasDisponiveis,
    atualizarFiltro,
    trocarPagina,
    trocarLimitePorPagina,
    trocarOrdenacao,
    limparFiltros,
  };
}
